import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDesktop, faPlus, faFolder, faTrash, faSearch, faThumbtack, faChevronDown, faChevronUp, faCheck, faArrowLeft, faArrowRight, faWandMagicSparkles, faCopy, faDownload } from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import { useAccount } from '../contexts/AccountContext';
import { useAccountSettings } from '../hooks/useAccountSettings';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { getUserData } from '../utils/userUtils';
import { getFolderDisplayLabel, isSubfoldersEnabled } from '../utils/folderHierarchy';
import { getPlatformName } from '../utils/platformNames';
import { isEnabledForAnyOfPlatforms } from '../utils/accountSettingPlatformList';
import { copyIneligibilityReason } from './CopyCampaignWorkflow/copyWorkflowApi';
// Channel access filtering is now handled by the backend
import { isGroupEditable } from './GroupEditModal';
import GroupCreativeUploadModal from './GroupCreativeUploadModal';
import ImportAllCampaignsModal from './ImportAllCampaignsModal';
import DataTable, { createActionColumn } from './DataTable';
import StatusBadge from './StatusBadge';
import { launchTemplatesFor } from '../utils/launchTemplates';
import './Pages.css';
import './CopyCampaignWorkflow/copy-workflow.css';

/**
 * CampaignsPage component for managing campaigns
 * Displays all campaigns for the current account organized by folders
 */
const CampaignsPage = ({
  // Flow-A entry point: invoked with an array of preselected campaign ids
  // ([] from the toolbar button, [campaign.id] from the row menu, or the
  // full multi-select set from the selection bar). The actual copy-campaign
  // form is a separate module (built elsewhere); until it's wired in here,
  // fall back to a no-op so this file stays fully functional standalone.
  onStartCopyCampaign = () => alert('Copy campaign — coming soon')
} = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAccountIdToUse, currentAccountName } = useAccount();
  const [user] = useState(() => getUserData()); // Get user data once on mount
  const accountId = getAccountIdToUse();
  const launchTemplates = launchTemplatesFor(currentAccountName);
  const { getSetting, settings: accountSettings } = useAccountSettings(accountId, user);
  // One-level folder nesting (Market -> Advertiser): adds a Sub-folder column
  const subfoldersEnabled = isSubfoldersEnabled(accountSettings);
  // Start-a-Campaign visibility — gated only on the `enable-v3-ui` account
  // flag (no role gate). Every role on the account sees it when the flag is on.
  const v3UiEnabled = getSetting('enable-v3-ui') === 'true';
  const optimizationWorkspaceEnabled = getSetting('performance-v2.0') === 'true';

  // No `enable-v3-ui-campaign-grid` read here — this page no longer has a
  // Campaign Grid entry point. The flag still gates the sidebar item in
  // NewSidebar.js, which is the only way in now.

  // Copy-campaign entry points (toolbar button, row kebab item, and the
  // multi-select checkbox column that exists only to feed them) — same
  // flag-only, per-account gating shape as the flags above. Frontend-only: the
  // /campaign/copy route and its API stay reachable, this just hides the ways
  // in to them from this page.
  const copyCampaignUiEnabled = getSetting('enable-v3-ui-copy-campaign') === 'true';
  const groupEditEnabled = getSetting('group-edit-enabled') === 'true';

  // Import All — gated on the SAME `import-campaign` account setting that
  // gates the single-campaign import form, not on a flag of its own. The two
  // are the same permission ("this account may pull campaigns in from these
  // platforms"); a separate flag would let them disagree, and an account that
  // can import one campaign has no reason not to import fifty. Narrowed to the
  // channels discovery can actually enumerate (services/platformCampaignDiscovery.js).
  const importAllEnabled = isEnabledForAnyOfPlatforms(
    accountSettings?.['import-campaign'],
    ['facebook', 'google-ads-search', 'google-ads-pmax', 'google-ads-demandgen'],
  );

  const [campaigns, setCampaigns] = useState([]);
  const [optimizationSaving, setOptimizationSaving] = useState(new Set());
  const [optimizationNotice, setOptimizationNotice] = useState(null);
  useEffect(() => setOptimizationNotice(null), [accountId]);
  useEffect(() => {
    if (!optimizationNotice) return;
    const timeout = setTimeout(() => setOptimizationNotice(null), 4000);
    return () => clearTimeout(timeout);
  }, [optimizationNotice]);
  const optimizationAccount = useRef(accountId);
  optimizationAccount.current = accountId;
  const optimizationRequests = useRef(new Set());
  const setCampaignOptimization = async (campaign, enabled) => {
    const requestKey = JSON.stringify([accountId, campaign.id]);
    if (!optimizationWorkspaceEnabled || optimizationRequests.current.has(requestKey)) return;
    optimizationRequests.current.add(requestKey);
    setOptimizationSaving(new Set(optimizationRequests.current));
    setOptimizationNotice(null);
    setError(null);
    try {
      await api.patch(getApiUrl('/api/v2/campaigns/optimization-eligibility'), {
        run_optimization: enabled, campaignIds: [campaign.id],
      }, { params: { accountId, userId: user?.userId || user?.id } });
      if (optimizationAccount.current === accountId) {
        setCampaigns(previous => previous.map(row => row.id === campaign.id
          ? { ...row, run_optimization: enabled } : row));
        setOptimizationNotice(`Run Optimization turned ${enabled ? 'on' : 'off'}.`);
      }
    } catch (error) {
      if (optimizationAccount.current === accountId) setError('Failed to update Run Optimization. Please try again.');
    } finally {
      optimizationRequests.current.delete(requestKey);
      setOptimizationSaving(new Set(optimizationRequests.current));
    }
  };
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const [downloadingTemplate, setDownloadingTemplate] = useState(null);
  const downloadLaunchTemplate = async (platform, filename) => {
    setDownloadingTemplate(platform);
    setError(null);
    try {
      const response = await api.get(getApiUrl(`/api/v2/campaigns/launch-templates/${platform}`), {
        params: { accountId, userId: user?.userId || user?.id },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError('Unable to download the launch template. Please check your account access and try again.');
    } finally {
      setDownloadingTemplate(null);
    }
  };

  // Folder filter state
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showFolderDropdown, setShowFolderDropdown] = useState(false);
  const [foldersLoading, setFoldersLoading] = useState(false);

  // Helper function to get persisted pagination state
  const getPersistedPaginationState = () => {
    // First check URL parameters for folder filter
    const urlFolderId = searchParams.get('folderId');

    try {
      const saved = sessionStorage.getItem('campaignsPage_paginationState');
      if (saved) {
        const parsed = JSON.parse(saved);
        console.log('🔄 Restored pagination state:', parsed);

        // Override with URL parameter if present
        if (urlFolderId) {
          parsed.selectedFolderId = urlFolderId;
        }

        return parsed;
      }
    } catch (error) {
      console.error('Error loading pagination state:', error);
    }
    return {
      currentPage: 1,
      itemsPerPage: 10,
      sortBy: 'created_at',
      sortOrder: 'desc',
      searchTerm: '',
      selectedFolderId: urlFolderId || null
    };
  };

  // Initialize pagination state from sessionStorage
  const initialState = getPersistedPaginationState();

  // Pagination and sorting state
  const [currentPage, setCurrentPage] = useState(initialState.currentPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialState.itemsPerPage);
  const [sortBy, setSortBy] = useState(initialState.sortBy);
  const [sortOrder, setSortOrder] = useState(initialState.sortOrder);
  const [searchTerm, setSearchTerm] = useState(initialState.searchTerm);
  // Search-as-you-type against GET /api/v2/campaigns — debounced through the
  // shared typeahead hook so a burst of keystrokes fetches only the final
  // term. The raw searchTerm still persists to sessionStorage instantly.
  const debouncedSearchTerm = useDebouncedValue(searchTerm);
  const [selectedFolderId, setSelectedFolderId] = useState(initialState.selectedFolderId);
  const [pagination, setPagination] = useState({
    currentPage: initialState.currentPage,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: initialState.itemsPerPage,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Dropdown state
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // Shared selection for copy and group edit.
  const [selectedIds, setSelectedIds] = useState(new Set());
  useEffect(() => { setSelectedIds(new Set()); setShowGroupEdit(false); }, [accountId]);
  const [showGroupEdit, setShowGroupEdit] = useState(false);
  const [showImportAll, setShowImportAll] = useState(false);

  // The campaigns behind the checked ids, and the Google Ads subset the group
  // editor can actually plan for. Derived rather than stored: selectedIds is the
  // single source of truth, and a stored copy would drift the moment the page
  // re-filters or re-sorts.
  const selectedCampaigns = campaigns.filter((c) => selectedIds.has(c.id));
  const groupEditableSelection = selectedCampaigns.filter(c => isGroupEditable(c) && (String(c.platform_campaign_id || '').trim() || c.copy_api_completed));
  const canGroupEditSelection = selectedIds.size > 1 && groupEditableSelection.length === selectedIds.size;
  const canCopySelection = selectedIds.size === 1 && selectedCampaigns.length === 1 && !copyIneligibilityReason(selectedCampaigns[0]);

  // Edit campaign inline state
  const [editingCampaignId, setEditingCampaignId] = useState(null);
  const [editingCampaignName, setEditingCampaignName] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const dropdownRefs = useRef({});

  // Move to folder modal state
  const [showMoveToFolderModal, setShowMoveToFolderModal] = useState(false);
  const [campaignToMove, setCampaignToMove] = useState(null);
  const [availableFolders, setAvailableFolders] = useState([]);
  const [selectedTargetFolder, setSelectedTargetFolder] = useState(null);
  const [isMoving, setIsMoving] = useState(false);
  const [folderSearchTerm, setFolderSearchTerm] = useState('');

  // Helper function to persist pagination state
  const persistPaginationState = (state) => {
    try {
      sessionStorage.setItem('campaignsPage_paginationState', JSON.stringify(state));
      console.log('💾 Saved pagination state:', state);
    } catch (error) {
      console.error('Error saving pagination state:', error);
    }
  };

  // Persist pagination state whenever it changes
  useEffect(() => {
    const state = {
      currentPage,
      itemsPerPage,
      sortBy,
      sortOrder,
      searchTerm,
      selectedFolderId
    };
    persistPaginationState(state);
  }, [currentPage, itemsPerPage, sortBy, sortOrder, searchTerm, selectedFolderId]);

  // Fetch folders when account changes
  useEffect(() => {
    if (accountId) {
      fetchFolders();
    }
  }, [accountId]);

  // Fetch campaigns when filters change (search rides the debounced term, so
  // the fetch fires once the user stops typing)
  useEffect(() => {
    if (accountId) {
      fetchCampaigns();
    }
  }, [accountId, currentPage, itemsPerPage, sortBy, sortOrder, debouncedSearchTerm, selectedFolderId]);

  // Handle success message from multi-platform campaign creation
  useEffect(() => {
    if (location.state?.fromCampaignCreation && location.state?.multiPlatform) {
      const { message, campaigns: createdCampaigns, summary } = location.state;

      if (message) {
        setSuccessMessage(message);
        console.log('🎉 Multi-platform campaigns created:', {
          total: summary?.total,
          created: summary?.created,
          campaigns: createdCampaigns?.map(c => c.name)
        });

        // Clear success message after 5 seconds
        const timer = setTimeout(() => {
          setSuccessMessage(null);
        }, 5000);

        // Clear location state to prevent showing message on refresh
        window.history.replaceState({}, document.title);

        return () => clearTimeout(timer);
      }
    }
  }, [location.state]);



  const fetchCampaigns = async () => {
    if (!accountId) return;

    try {
      setIsLoading(true);
      const params = {
        accountId,
        userId: user?.userId || user?.id,
        page: currentPage,
        limit: itemsPerPage,
        sortBy: sortBy,
        sortOrder: sortOrder,
        search: debouncedSearchTerm
      };

      // Add folderId filter if a folder is selected
      if (selectedFolderId) {
        params.folderId = selectedFolderId;
      }

      const response = await api.get(getApiUrl('/api/v2/campaigns'), {
        params
      });

      const campaignsData = response.data.campaigns || response.data || [];

      // Page-overflow recovery: if a stale currentPage (e.g. restored from
      // sessionStorage on initial load) lands past the end of the filtered
      // result, the backend returns an empty page even though there are
      // rows. Detect that and re-fetch from page 1 instead of leaving the
      // user staring at an empty table.
      const pg = response.data.pagination;
      if (pg && pg.totalItems > 0 && currentPage > (pg.totalPages || 1) && currentPage !== 1) {
        setCurrentPage(1);
        return; // useEffect will re-fire fetchCampaigns with page=1
      }

      // Channel access filtering is now handled by the backend
      setCampaigns(campaignsData);

      // Use backend pagination data (filtering is handled server-side)
      if (pg) {
        setPagination(pg);
      } else {
        // Fallback for old API format
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalItems: campaignsData.length,
          itemsPerPage: campaignsData.length,
          hasNextPage: false,
          hasPrevPage: false
        });
      }
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      setError('Failed to load campaigns');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFolders = async () => {
    if (!accountId) return;

    try {
      setFoldersLoading(true);
      const response = await api.get(getApiUrl('/api/v2/folders'), {
        params: {
          accountId,
          userId: user?.userId || user?.id,
          limit: 100 // Get all folders for the dropdown
        }
      });

      const foldersData = response.data.folders || response.data || [];
      setFolders(foldersData);

      // If we have a selectedFolderId but no selectedFolder object, find and set it
      if (selectedFolderId && !selectedFolder) {
        const folder = foldersData.find(f => f.id === selectedFolderId);
        if (folder) {
          setSelectedFolder(folder);
        } else {
          // If the selected folder no longer exists, clear the selection
          setSelectedFolderId(null);
          setSelectedFolder(null);
        }
      }
    } catch (err) {
      console.error('Error fetching folders:', err);
      // Don't set error state for folders as it's not critical
    } finally {
      setFoldersLoading(false);
    }
  };

  // Handle sorting when a column header is clicked
  const requestSort = (key) => {
    let direction = 'desc';
    if (sortBy === key && sortOrder === 'desc') {
      direction = 'asc';
    }
    setSortBy(key);
    setSortOrder(direction);
    setCurrentPage(1);
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Handle folder filter
  const handleFolderSelect = (folder) => {
    if (folder) {
      setSelectedFolder(folder);
      setSelectedFolderId(folder.id);
      // Update URL parameter
      setSearchParams({ folderId: folder.id });
    } else {
      setSelectedFolder(null);
      setSelectedFolderId(null);
      // Remove URL parameter
      setSearchParams({});
    }
    setCurrentPage(1); // Reset to first page when changing folder filter
    setShowFolderDropdown(false);
  };



  // Handle edit campaign
  const handleEditCampaign = (campaign) => {
    setEditingCampaignId(campaign.id);
    setEditingCampaignName(campaign.name);
    setError(null);
  };

  const handleUpdateCampaign = async () => {
    if (!editingCampaignName.trim()) return;

    try {
      setIsUpdating(true);
      setError(null);

      const response = await api.put(getApiUrl(`/api/v2/campaigns/${editingCampaignId}`), {
        name: editingCampaignName.trim()
      }, {
        params: { accountId, userId: user?.userId || user?.id }
      });

      if (response.status === 200) {
        // Update the campaign in local state
        setCampaigns(prev => prev.map(c =>
          c.id === editingCampaignId
            ? { ...c, name: editingCampaignName.trim() }
            : c
        ));

        // Exit edit mode
        setEditingCampaignId(null);
        setEditingCampaignName('');
      }
    } catch (err) {
      console.error('Error updating campaign:', err);
      const errorMessage = err.response?.data?.error || 'Failed to update campaign';
      setError(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingCampaignId(null);
    setEditingCampaignName('');
    setError(null);
  };

  // Handle move to folder
  const handleMoveToFolder = async (campaign) => {
    setCampaignToMove(campaign);
    setShowMoveToFolderModal(true);
    setSelectedTargetFolder(null);
    setFolderSearchTerm('');

    // Fetch available folders
    try {
      const response = await api.get(getApiUrl('/api/v2/folders'), {
        params: {
          accountId,
          userId: user?.userId || user?.id,
          page: 1,
          limit: 100, // Get all folders
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });

      // Filter out the current folder
      const folders = (response.data.folders || response.data || []).filter(
        f => f.id !== campaign.folder_id
      );
      setAvailableFolders(folders);
    } catch (err) {
      console.error('Error fetching folders:', err);
      setError('Failed to load folders');
    }
  };

  const handleConfirmMove = async () => {
    if (!selectedTargetFolder || !campaignToMove) return;

    setIsMoving(true);
    try {
      const response = await api.patch(
        getApiUrl(`/api/v2/campaigns/${campaignToMove.id}`),
        { folder_id: selectedTargetFolder.id },
        {
          params: {
            accountId,
            userId: user?.userId || user?.id
          }
        }
      );

      if (response.status === 200) {
        setSuccessMessage(`Campaign moved to "${selectedTargetFolder.name}" successfully`);
        setTimeout(() => setSuccessMessage(null), 3000);

        // Close modal
        setShowMoveToFolderModal(false);
        setCampaignToMove(null);
        setSelectedTargetFolder(null);

        // Refresh campaigns list to get the latest state from server
        await fetchCampaigns();
      }
    } catch (err) {
      console.error('Error moving campaign:', err);
      const errorMessage = err.response?.data?.error || 'Failed to move campaign';
      setError(errorMessage);
    } finally {
      setIsMoving(false);
    }
  };

  const handleCancelMove = () => {
    setShowMoveToFolderModal(false);
    setCampaignToMove(null);
    setSelectedTargetFolder(null);
    setFolderSearchTerm('');
  };

  // Handle dropdown toggle
  const toggleDropdown = (campaignId, e) => {
    e.preventDefault();
    e.stopPropagation();

    if (openDropdownId === campaignId) {
      setOpenDropdownId(null);
      return;
    }

    // Calculate dropdown position
    const buttonRect = e.currentTarget.getBoundingClientRect();
    const dropdownWidth = 160; // 10rem
    // Approximate height for 4 items (Delete, Pin/Unpin, Move to Folder, Copy
    // to new campaign): ~40px/item (.sidebar-dropdown-item padding 0.5rem
    // top+bottom + margin 0.125rem top+bottom + ~20px line content, per
    // DataTable.css) x 4 = 160.
    const dropdownHeight = 160;

    let top = buttonRect.bottom + 5;
    let left = buttonRect.right - dropdownWidth;

    // Adjust if dropdown would go off screen
    if (left < 10) {
      left = 10;
    }

    if (top + dropdownHeight > window.innerHeight - 10) {
      top = buttonRect.top - dropdownHeight - 5;
    }

    setDropdownPosition({ top, left });
    setOpenDropdownId(campaignId);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId && !Object.values(dropdownRefs.current).some(ref =>
        ref && ref.contains(event.target)
      )) {
        setOpenDropdownId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdownId]);

  // Close folder dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFolderDropdown && !event.target.closest('.folder-filter-container')) {
        setShowFolderDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFolderDropdown]);

  // Format date helper
  const formatDate = (campaign) => {
    const date = campaign.createdAt || campaign.created_at;
    if (!date) return 'Unknown';

    const campaignDate = new Date(date);
    const now = new Date();

    // Check if it's the same calendar day in local timezone
    const isToday = campaignDate.toDateString() === now.toDateString();

    // Check if it's yesterday
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = campaignDate.toDateString() === yesterday.toDateString();

    if (isToday) {
      return `Today ${campaignDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    } else if (isYesterday) {
      return `Yesterday ${campaignDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    } else {
      // For dates more than 1 day ago, show the date
      const diffTime = Math.abs(now - campaignDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays <= 7) {
        return `${diffDays} days ago`;
      } else {
        return campaignDate.toLocaleDateString();
      }
    }
  };

  // StatusBadge component is now imported from ./StatusBadge

  // Platform display name - normalize platform names for display.
  // Extracted to utils/platformNames.js so CopyCampaignEntryPicker.js's
  // picker table formats the same raw `destination` values identically,
  // instead of falling back to a raw slug it doesn't recognize.

  // Multi-select helpers (Flow-A: Copy campaign)
  // Selection is independent of which action can process the selected rows.
  const toggleSelectCampaign = (campaignId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(campaignId)) next.delete(campaignId);
      else next.add(campaignId);
      return next;
    });
  };

  const toggleSelectAllVisible = (eligible) => {
    setSelectedIds((prev) => {
      const allChecked = eligible.length > 0 && eligible.every((c) => prev.has(c.id));
      return allChecked ? new Set() : new Set(eligible.map((c) => c.id));
    });
  };

  const columns = [
    {
      key: 'select',
      label: '',
      width: '44px',
      className: 'select-cell',
      headerRender: () => {
        const eligible = campaigns;
        if (eligible.length === 0) return null;
        const allChecked = eligible.every((c) => selectedIds.has(c.id));
        return (
          <input
            type="checkbox"
            className="row-select-checkbox"
            checked={allChecked}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggleSelectAllVisible(eligible)}
            title={allChecked ? 'Clear selection' : 'Select every campaign on this page'}
            aria-label="Select all campaigns on this page"
          />
        );
      },
      render: (campaign) => {
        return (
          <input
            type="checkbox"
            className="row-select-checkbox"
            checked={selectedIds.has(campaign.id)}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggleSelectCampaign(campaign.id)}
            aria-label={`Select ${campaign.name}`}
          />
        );
      }
    },
    {
      key: 'name',
      label: 'Campaign Name',
      width: '1fr',
      sortable: true,
      resizable: true,
      render: (campaign) => (
        <div className="campaign-name-content">
          {campaign.is_pinned && (
            <FontAwesomeIcon
              icon={faThumbtack}
              className="pinned-icon"
              title="Pinned campaign"
            />
          )}
          {editingCampaignId === campaign.id ? (
            <div
              className="inline-edit-container"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="text"
                value={editingCampaignName}
                onChange={(e) => setEditingCampaignName(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleUpdateCampaign();
                  } else if (e.key === 'Escape') {
                    handleCancelEdit();
                  }
                }}
                className="inline-edit-input"
                autoFocus
                disabled={isUpdating}
              />
              <div className="inline-edit-actions">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleUpdateCampaign();
                  }}
                  disabled={!editingCampaignName.trim() || isUpdating}
                  className="inline-edit-btn save-btn"
                  title="Save"
                >
                  {isUpdating ? '⟳' : '✓'}
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCancelEdit();
                  }}
                  disabled={isUpdating}
                  className="inline-edit-btn cancel-btn"
                  title="Cancel"
                >
                  ✕
                </button>
              </div>
            </div>
          ) : (
            <div className="campaign-name-container">
              <span
                className="campaign-name-text"
                title="Campaign name"
              >
                {campaign.name}
              </span>
              <div className="campaign-name-actions">
                <button
                  className="edit-name-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleEditCampaign(campaign);
                  }}
                  title="Edit campaign name"
                >
                  ✏️
                </button>
              </div>
            </div>
          )}
        </div>
      )
    },
    {
      key: 'folder_name',
      label: 'Folder',
      width: '200px',
      sortable: true,
      className: 'folder-info',
      render: (campaign) => (
        <span className="folder-info">
          <FontAwesomeIcon icon={faFolder} />
          {/* For a campaign living in a sub-folder this shows the Market
              (parent); the sub-folder itself goes in the Sub-folder column */}
          {campaign.folder_parent_name || campaign.folderName || campaign.folder_name || 'No folder'}
        </span>
      )
    },
    ...(subfoldersEnabled ? [{
      key: 'sub_folder',
      label: 'Sub-folder',
      width: '180px',
      sortable: false,
      className: 'folder-info',
      render: (campaign) => campaign.folder_parent_name ? (
        <span className="folder-info">
          <FontAwesomeIcon icon={faFolder} />
          {campaign.folder_name}
        </span>
      ) : (
        <span className="campaign-count-zero">—</span>
      )
    }] : []),
    {
      key: 'destination',
      label: 'Platform',
      width: '140px',
      sortable: true,
      className: 'platform-info',
      render: (campaign) => getPlatformName(campaign.destination)
    },
    {
      key: 'status',
      label: 'Status',
      width: '220px',
      sortable: true,
      className: 'status-info',
      render: (campaign) => <StatusBadge status={campaign.status} />
    },
    {
      key: 'created_at',
      label: 'Created',
      width: '180px',
      sortable: true,
      className: 'created-date',
      render: (campaign) => formatDate(campaign)
    },
    createActionColumn(null, dropdownRefs, openDropdownId, toggleDropdown),
    {
      key: 'run_optimization', label: 'Run Optimization', width: '160px',
      render: campaign => optimizationWorkspaceEnabled ? (
        <div
          className="campaign-optimization-options"
          role="radiogroup"
          aria-label={`Run Optimization for ${campaign.name}`}
          aria-busy={optimizationSaving.has(JSON.stringify([accountId, campaign.id]))}
          onClick={event => event.stopPropagation()}
        >
          {[false, true].map(enabled => (
            <label key={String(enabled)}>
              <input
                type="radio"
                name={`run-optimization-${accountId}-${campaign.id}`}
                value={enabled ? 'on' : 'off'}
                checked={(campaign.run_optimization === true) === enabled}
                disabled={optimizationSaving.has(JSON.stringify([accountId, campaign.id]))}
                onChange={() => setCampaignOptimization(campaign, enabled)}
              />
              {enabled ? 'On' : 'Off'}
            </label>
          ))}
        </div>
      ) : <span>{campaign.run_optimization === true ? 'On' : 'Off'}</span>,
    }
  ];



  // Dropdown Menu Component using Portal (following sidebar pattern)
  const DropdownMenu = ({ campaign, onClose }) => {
    if (!campaign) return null;

    const handleDelete = async (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (window.confirm(`Are you sure you want to delete "${campaign.name}"?`)) {
        try {
          await api.delete(getApiUrl(`/api/v2/campaigns/${campaign.id}`), {
            params: {
              accountId,
              userId: user?.userId || user?.id
            }
          });
          setCampaigns(prev => prev.filter(c => c.id !== campaign.id));

          // Update pagination totalItems count
          setPagination(prev => ({
            ...prev,
            totalItems: Math.max(0, prev.totalItems - 1)
          }));
        } catch (error) {
          console.error('Error deleting campaign:', error);
          setError('Failed to delete campaign. Please try again.');
        }
      }
      onClose();
    };

    const handlePin = async (e) => {
      e.preventDefault();
      e.stopPropagation();

      try {
        const response = await api.patch(getApiUrl(`/api/v2/campaigns/${campaign.id}`), {
          is_pinned: !campaign.is_pinned
        }, {
          params: {
            accountId,
            userId: user?.userId || user?.id
          }
        });

        if (response.status === 200) {
          // Update the campaign's pin status
          const updatedCampaigns = campaigns.map(c =>
            c.id === campaign.id ? { ...c, is_pinned: !c.is_pinned } : c
          );

          // Sort campaigns with pinned items first, then by current sort criteria
          const sortedCampaigns = updatedCampaigns.sort((a, b) => {
            // Pinned campaigns first
            if (a.is_pinned && !b.is_pinned) return -1;
            if (!a.is_pinned && b.is_pinned) return 1;

            // Within same pin status, sort by current sort criteria
            let comparison = 0;

            if (sortBy === 'name') {
              comparison = a.name.localeCompare(b.name);
            } else if (sortBy === 'status') {
              comparison = (a.status || '').localeCompare(b.status || '');
            } else if (sortBy === 'destination') {
              comparison = (a.destination || '').localeCompare(b.destination || '');
            } else if (sortBy === 'folder_name') {
              // Match the backend's Market-first ordering for nested folders
              comparison = (a.folder_parent_name || a.folder_name || '').localeCompare(b.folder_parent_name || b.folder_name || '')
                || (a.folder_name || '').localeCompare(b.folder_name || '');
            } else if (sortBy === 'created_at') {
              comparison = new Date(a.created_at) - new Date(b.created_at);
            } else if (sortBy === 'updated_at') {
              comparison = new Date(a.updated_at) - new Date(b.updated_at);
            } else if (sortBy === 'is_pinned') {
              comparison = (a.is_pinned ? 1 : 0) - (b.is_pinned ? 1 : 0);
            }

            // Apply sort order (asc/desc)
            return sortOrder === 'asc' ? comparison : -comparison;
          });

          setCampaigns(sortedCampaigns);
        }
      } catch (error) {
        console.error('Error updating campaign pin status:', error);
        setError('Failed to update campaign. Please try again.');
      }
      onClose();
    };

    const handleMoveClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleMoveToFolder(campaign);
      onClose();
    };

    const handleCopyClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      onStartCopyCampaign([campaign.id]);
      onClose();
    };

    return (
      <div
        className="sidebar-dropdown-menu"
        style={{
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onMouseDown={handleDelete}
          className="sidebar-dropdown-item delete"
        >
          <FontAwesomeIcon icon={faTrash} />
          Delete
        </button>
        <button
          onMouseDown={handlePin}
          className="sidebar-dropdown-item"
        >
          <FontAwesomeIcon icon={faThumbtack} />
          {campaign.is_pinned ? 'Unpin' : 'Pin'}
        </button>
        <button
          onMouseDown={handleMoveClick}
          className="sidebar-dropdown-item"
        >
          <FontAwesomeIcon icon={faArrowRight} />
          Move to Folder
        </button>
        {copyCampaignUiEnabled && (() => {
          // Same fail-early gate as the checkbox column: an ineligible
          // campaign (no channel / never pushed / unsupported platform) keeps
          // the item visible but disabled, with the reason on the tooltip —
          // hiding it entirely would read as a missing feature.
          const copyIneligible = copyIneligibilityReason(campaign);
          return (
            <button
              onMouseDown={copyIneligible ? undefined : handleCopyClick}
              className="sidebar-dropdown-item cw-entry-kebab-item"
              disabled={!!copyIneligible}
              title={copyIneligible || undefined}
              style={copyIneligible ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
            >
              <FontAwesomeIcon icon={faCopy} />
              Copy to new campaign…
            </button>
          );
        })()}
      </div>
    );
  };

  if (!accountId) {
    return (
      <div className="page-container">
        <div className="page-error">
          <p>Please select an account to view campaigns.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-left">
          {selectedFolder && (
            <div className="breadcrumb-nav">
              <button
                onClick={() => handleFolderSelect(null)}
                className="breadcrumb-back-btn"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                All Folders
              </button>
              {selectedFolder.parent_name && (
                <>
                  <span className="breadcrumb-separator">›</span>
                  <span className="breadcrumb-current">{selectedFolder.parent_name}</span>
                </>
              )}
              <span className="breadcrumb-separator">›</span>
              <span className="breadcrumb-current">{selectedFolder.name}</span>
            </div>
          )}
          <h1>
            Campaigns
            {selectedFolder && <span className="folder-context">in {getFolderDisplayLabel(selectedFolder)}</span>}
            <span className="count-badge">{pagination.totalItems || campaigns.length}</span>
          </h1>
          {!selectedFolder && (
            <p>Manage all your advertising campaigns</p>
          )}
          {launchTemplates.length > 0 && (
            <nav className="campaign-launch-templates" aria-label="Launch templates">
              <span>Launch templates:</span>
              {launchTemplates.map(({ platform, filename, label }) => (
                <button
                  key={platform}
                  type="button"
                  disabled={downloadingTemplate !== null}
                  onClick={() => downloadLaunchTemplate(platform, filename)}
                >
                  <FontAwesomeIcon icon={faDownload} /> {downloadingTemplate === platform ? 'Downloading…' : label}
                </button>
              ))}
            </nav>
          )}
        </div>
        <div className="header-right">
          {/* Folder Filter */}
          <div className="folder-filter-container">
            <button
              type="button"
              className={`folder-filter-button ${showFolderDropdown ? 'active' : ''}`}
              onClick={() => setShowFolderDropdown(!showFolderDropdown)}
              disabled={foldersLoading}
            >
              <FontAwesomeIcon icon={faFolder} />
              <span>
                {selectedFolder ? getFolderDisplayLabel(selectedFolder) : 'All Folders'}
              </span>
              <FontAwesomeIcon
                icon={showFolderDropdown ? faChevronUp : faChevronDown}
                className="dropdown-arrow"
              />
            </button>

            {showFolderDropdown && (
              <div className="folder-filter-dropdown">
                <button
                  type="button"
                  className={`folder-filter-option ${!selectedFolder ? 'selected' : ''}`}
                  onClick={() => handleFolderSelect(null)}
                >
                  <FontAwesomeIcon icon={faFolder} />
                  <span>All Folders</span>
                </button>

                <div className="folder-filter-divider" />

                {folders.map(folder => (
                  <button
                    key={folder.id}
                    type="button"
                    className={`folder-filter-option ${selectedFolder?.id === folder.id ? 'selected' : ''}`}
                    onClick={() => handleFolderSelect(folder)}
                  >
                    <FontAwesomeIcon icon={faFolder} />
                    <span>{getFolderDisplayLabel(folder)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="search-box">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search campaigns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <button
            className="btn btn-primary"
            onClick={() => navigate('/campaign/new')}
          >
            <FontAwesomeIcon icon={faPlus} />
            New Campaign
          </button>
          {importAllEnabled && (
            <button
              className="btn btn-secondary"
              onClick={() => setShowImportAll(true)}
              title="Import every campaign on a Facebook or Google Ads account, filtered by status"
            >
              <FontAwesomeIcon icon={faDownload} />
              Import All
            </button>
          )}
          {copyCampaignUiEnabled && (
          <button
            className="btn btn-secondary cw-entry-btn-secondary"
            disabled={selectedIds.size > 0 && !canCopySelection}
            onClick={() => onStartCopyCampaign(Array.from(selectedIds))}
            title={selectedIds.size > 0
              ? 'Copy the selected campaign'
              : 'Copy an existing campaign into a new campaign'}
          >
            <FontAwesomeIcon icon={faCopy} />
            {selectedIds.size > 0 ? 'Copy campaign…' : 'Copy campaign'}
          </button>
          )}
          {groupEditEnabled && selectedIds.size > 1 && (
            <button className="btn btn-secondary" onClick={() => setShowGroupEdit(true)}
              disabled={!canGroupEditSelection}
              title={canGroupEditSelection ? 'Choose an action for the selected campaigns' : 'Select launched Google Ads campaigns to upload creatives'}>
              <FontAwesomeIcon icon={faWandMagicSparkles} />
              Group Edit ({selectedIds.size})
            </button>
          )}
          {v3UiEnabled && (
            <button
              className="btn btn-primary btn-start-campaign"
              onClick={() => navigate('/campaign/v3/start')}
              title="Start a campaign from a brief, creatives, cohort, or product"
            >
              <FontAwesomeIcon icon={faWandMagicSparkles} />
              Campaign Wizard
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="page-error">
          <p>{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="page-success" style={{
          padding: '12px 20px',
          marginBottom: '20px',
          backgroundColor: '#d4edda',
          border: '1px solid #c3e6cb',
          borderRadius: '4px',
          color: '#155724'
        }}>
          <p style={{ margin: 0 }}>✅ {successMessage}</p>
        </div>
      )}

      <div className="campaign-optimization-notice" role="status" aria-live="polite" aria-atomic="true">
        {optimizationWorkspaceEnabled && optimizationNotice && (
          <div className="campaign-optimization-popup">
            <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
            <span>{optimizationNotice}</span>
          </div>
        )}
      </div>

      <DataTable
        data={campaigns}
        columns={columns}
        pagination={pagination}
        onSort={requestSort}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        onRowClick={(campaign, e) => {
          // Don't navigate if clicking on action button, inline edit elements, or edit button
          if (e.target.closest('.dropdown-container') ||
              e.target.closest('.sidebar-action-button') ||
              e.target.closest('.inline-edit-container') ||
              e.target.closest('.campaign-name-actions')) {
            return;
          }
          // GT campaigns inserted by CLW after CTS review (but not yet
          // launched) route back to CLW so the user can resume + Approve.
          // Status='draft' alone isn't enough — it's the schema default,
          // so launched GT campaigns also wear it until they reach
          // 'active'. The distinguishing signal is orchestration_chat_id:
          // set as soon as the GT dispatch happens (v2/campaigns POST),
          // so NULL means "still a pre-launch draft from CLW".
          if (
            campaign.status === 'draft'
            && !campaign.orchestration_chat_id
            && (campaign.destination || '').toLowerCase() === 'groundtruth'
          ) {
            navigate(`/campaign/v3/start?campaign=${campaign.id}`);
            return;
          }
          // A planning-mode row built by the COPY flow reopens that flow at its
          // Push step instead of the detail page: it isn't a campaign anyone is
          // managing yet, it's a half-finished copy run waiting to be pushed,
          // and the detail page offers no way back into the flow that made it.
          //
          // Gated on copy_workflow — stamped only by POST /copy — so
          // planning-mode campaigns from CLW, fast-track and import keep going
          // to the detail page exactly as before. Also requires the stored
          // source id: builds created before it was persisted can't rehydrate
          // their source campaign, and a Push step with no source renders an
          // empty changeset, so those keep the old behaviour too.
          //
          // AND only while the copy is still unlaunched. planning_mode is
          // stamped on the orchestration at BUILD time and never cleared, so it
          // stays true forever — without this a copy that had already been
          // pushed reopened on a dead Push step reading "Launch 0 built
          // campaigns / Nothing is ready to launch" instead of opening the
          // campaign it had become.
          //
          // TWO launched signals, because platform_campaign_id on its own was
          // wrong: nothing stamps it when a copy's launch completes
          // (fastTrackService.updateCampaignStatusOnCompletion writes only the
          // status), and its one writer — checkAndUpdateCampaignStatus, inside
          // GET /api/v2/orchestration/:id/status — runs on a request rather than
          // on completion. So a fully launched Search/PMax copy sat here with a
          // NULL platform_campaign_id and this branch fired, dumping the user on
          // the dead Push step; it only started opening the detail page after
          // they had reached that page some other way (the completion email's
          // "View Campaign" link) and its polling stamped the id as a side
          // effect. copy_api_completed closes that hole: the api step IS the
          // platform create, and for a planning copy it only runs once
          // planning-launch resumes the orchestration.
          //
          // Deliberately NOT the build's alreadyLaunched flag. That means
          // "this orchestration is no longer awaiting review", which is also
          // true of a copy whose BUILD finished and has never been pushed —
          // see the WHY NOT block in CopyPlanStep. Using it here would send
          // unlaunched drafts to the detail page, which is the opposite bug.
          if (
            campaign.planning_mode
            && campaign.copy_workflow
            && campaign.copy_source_campaign_id
            && !campaign.platform_campaign_id
            && !campaign.copy_api_completed
          ) {
            const params = new URLSearchParams({
              campaignIds: campaign.copy_source_campaign_id,
              build: campaign.id,
              step: 'push',
            });
            navigate(`/campaign/copy?${params.toString()}`);
            return;
          }
          // From the campaigns list, every other campaign opens the current
          // detail page — including planning-mode ones, which surface the "Open
          // in new UI" button there to jump into the Campaign Launch Workflow.
          // CLW is opened directly only from the Launch board (which navigates
          // with ?from=launch), so the back button there returns to the board.
          navigate(`/campaigns/${campaign.id}`);
        }}
        sortBy={sortBy}
        sortOrder={sortOrder}
        isLoading={isLoading}
        emptyMessage={
          <div className="empty-state">
            <FontAwesomeIcon icon={faDesktop} className="empty-state-icon" />
            <h3>No campaigns yet</h3>
            <p>Create your first campaign to get started</p>
          </div>
        }
        className="campaigns-table"
        storageKey="campaigns"
      />

      {/* Dropdown Menu */}
      {openDropdownId && (
        <DropdownMenu
          campaign={campaigns.find(c => c.id === openDropdownId)}
          onClose={() => setOpenDropdownId(null)}
        />
      )}

      {/* Move to Folder Modal */}
      {showMoveToFolderModal && campaignToMove && (
        <div className="modal-overlay" onClick={handleCancelMove}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <FontAwesomeIcon icon={faArrowRight} style={{ marginRight: '8px' }} />
                Move Campaign to Folder
              </h2>
              <button className="close-button" onClick={handleCancelMove}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="campaign-info" style={{ marginBottom: '20px', padding: '12px', background: '#f9fafb', borderRadius: '6px' }}>
                <strong>{campaignToMove.name}</strong>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  Current folder: {campaignToMove.folder_parent_name
                    ? `${campaignToMove.folder_parent_name} / ${campaignToMove.folder_name}`
                    : campaignToMove.folder_name || 'Unknown'}
                </div>
              </div>

              <div className="form-group">
                <label>Select Target Folder</label>
                <input
                  type="text"
                  placeholder="Search folders..."
                  value={folderSearchTerm}
                  onChange={(e) => setFolderSearchTerm(e.target.value)}
                  className="form-control"
                  style={{ marginBottom: '12px' }}
                />

                <div className="folder-list" style={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  background: 'white'
                }}>
                  {availableFolders
                    .filter(folder =>
                      getFolderDisplayLabel(folder).toLowerCase().includes(folderSearchTerm.toLowerCase())
                    )
                    .map(folder => (
                      <button
                        key={folder.id}
                        type="button"
                        className={`folder-option ${selectedTargetFolder?.id === folder.id ? 'selected' : ''}`}
                        onClick={() => setSelectedTargetFolder(folder)}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: 'none',
                          background: selectedTargetFolder?.id === folder.id ? '#EBF8FF' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease',
                          fontSize: '14px',
                          textAlign: 'left'
                        }}
                      >
                        <FontAwesomeIcon icon={faFolder} />
                        <span style={{ flex: 1 }}>{getFolderDisplayLabel(folder)}</span>
                        {selectedTargetFolder?.id === folder.id && (
                          <FontAwesomeIcon icon={faCheck} style={{ color: '#10B981' }} />
                        )}
                      </button>
                    ))}
                  {availableFolders.filter(folder =>
                    getFolderDisplayLabel(folder).toLowerCase().includes(folderSearchTerm.toLowerCase())
                  ).length === 0 && (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                      {folderSearchTerm ? 'No folders found matching your search' : 'No other folders available'}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={handleCancelMove}
                disabled={isMoving}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleConfirmMove}
                disabled={!selectedTargetFolder || isMoving}
              >
                {isMoving ? 'Moving...' : 'Move Campaign'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showImportAll && (
        <ImportAllCampaignsModal
          accountId={accountId}
          user={user}
          folders={folders}
          accountSettings={accountSettings}
          defaultFolderId={selectedFolder?.id || null}
          onClose={() => setShowImportAll(false)}
          // Imports run in the background, so the rows exist (as 'active',
          // filling in) the moment the route returns — refetch rather than
          // making the user reload to see what they just started.
          onImported={() => fetchCampaigns()}
        />
      )}
      {groupEditEnabled && showGroupEdit && (
        <GroupCreativeUploadModal
          key={accountId}
          campaigns={selectedCampaigns}
          accountId={accountId}
          userId={user?.userId || user?.id}
          onClose={() => setShowGroupEdit(false)}
        />
      )}
    </div>
  );
};

export default CampaignsPage;
